import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { gsap } from "./lib/anim";
import { Icon } from "./components/Icons";
import { Plate } from "./components/ui";
import { STUDIO } from "./lib/store";
import { useSession, useIsAdmin } from "./lib/auth";
import { apiIntegrations, apiSetMindmapKey, apiClearMindmapKey, apiPingMindmap, type Integrations as Info } from "./lib/authApi";

/* A private, admin-only page to connect the Task Mind Map — enter the key here
   instead of pasting it into a chat or running wrangler. It's stored server-side
   (D1) and never read back to the browser. */
export default function Integrations() {
  const nav = useNavigate();
  const session = useSession();
  const isAdmin = useIsAdmin();
  const root = useRef<HTMLDivElement>(null);
  const [info, setInfo] = useState<Info | null>(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState<"save" | "test" | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);

  async function refresh() { setInfo(await apiIntegrations()); }
  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin]);
  useEffect(() => {
    const ctx = gsap.context(() => { gsap.from(".ig-el", { y: 14, opacity: 0, duration: 0.5, ease: "power3.out", stagger: 0.06 }); }, root);
    return () => ctx.revert();
  }, []);

  async function save() {
    if (key.trim().length < 6) { setMsg({ tone: "warn", text: "That key looks too short." }); return; }
    setBusy("save"); setMsg(null);
    try {
      await apiSetMindmapKey(key);
      setKey("");
      await refresh();
      // confirm it actually authenticates against the Mind Map
      const p = await apiPingMindmap();
      setMsg(p.ok ? { tone: "ok", text: "Saved and connected — the Mind Map accepted the key." }
        : { tone: "warn", text: `Saved, but the test failed: ${p.reason || p.error || "no response"}. Double-check it's the manager key.` });
    } catch (e: any) {
      setMsg({ tone: "warn", text: e?.message || "Couldn't save the key." });
    } finally { setBusy(null); }
  }
  async function test() {
    setBusy("test"); setMsg(null);
    const p = await apiPingMindmap();
    setMsg(p.ok ? { tone: "ok", text: "Connected — the Mind Map accepted the key." } : { tone: "warn", text: p.reason || p.error || "The test failed." });
    setBusy(null);
  }
  async function clear() {
    await apiClearMindmapKey(); setMsg(null); await refresh();
  }

  const connected = !!info?.mindmap;

  return (
    <div ref={root} style={{ minHeight: "100vh", background: "var(--paper)", display: "grid", placeItems: "center", padding: "clamp(24px,6vw,64px)", position: "relative" }}>
      <div style={{ position: "absolute", top: "-14%", right: "-10%", width: 420, height: 420, opacity: 0.35, filter: "blur(3px)", pointerEvents: "none" }}>
        <div style={{ borderRadius: "50%", overflow: "hidden", width: "100%", height: "100%" }}><Plate hue={26} h={420} seed={4} /></div>
      </div>

      <div style={{ width: "100%", maxWidth: 520, zIndex: 1 }}>
        <div className="ig-el row" style={{ gap: 12, marginBottom: 22 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center" }}><Icon.logo width={22} /></div>
          <div><div className="serif" style={{ fontSize: 20, lineHeight: 1 }}>{STUDIO.name}</div><div className="kicker" style={{ fontSize: 9.5, marginTop: 3 }}>Integrations</div></div>
        </div>

        {!session ? (
          <div className="ig-el card pad">
            <h1 className="serif" style={{ fontSize: 24 }}>Sign in first</h1>
            <p className="muted" style={{ fontSize: 14, marginTop: 8 }}>This page is for the studio admin. Sign in and come back.</p>
            <button className="btn accent" style={{ marginTop: 16 }} onClick={() => nav("/login")}>Sign in <Icon.arrow width={15} /></button>
          </div>
        ) : !isAdmin ? (
          <div className="ig-el card pad">
            <h1 className="serif" style={{ fontSize: 24 }}>Admins only</h1>
            <p className="muted" style={{ fontSize: 14, marginTop: 8 }}>Only the studio admin can change integrations. Ask {STUDIO.principal} to connect the Mind Map.</p>
            <button className="btn ghost" style={{ marginTop: 16 }} onClick={() => nav(-1)}><Icon.back width={14} />Go back</button>
          </div>
        ) : (
          <div className="ig-el card pad" style={{ boxShadow: "var(--shadow-lg)" }}>
            <div className="row between" style={{ marginBottom: 8 }}>
              <h1 className="serif" style={{ fontSize: 25 }}>Task Mind Map</h1>
              {connected
                ? <span className="badge ok"><span className="dot" />Connected</span>
                : <span className="badge"><span className="dot" />Not connected</span>}
            </div>
            <p className="muted" style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 18 }}>
              Paste the admin key you use to sign into <b>mindmap.studiogy.design</b> as manager. It's stored securely on the server and never shown again — sending drawings, milestones and the work programme will start working straight away.
            </p>

            <div className="field">
              <label>Mind Map admin key</label>
              <input className="input mono" type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)}
                placeholder={connected ? "•••••••• (a key is set — enter a new one to replace it)" : "paste the key"} onKeyDown={(e) => e.key === "Enter" && save()} />
            </div>

            {msg && (
              <div className="row" style={{ gap: 8, marginTop: 12, alignItems: "flex-start" }}>
                {msg.tone === "ok" ? <Icon.checkCircle width={16} style={{ color: "var(--ok)", flexShrink: 0, marginTop: 1 }} /> : <Icon.bell width={15} style={{ color: "var(--warn)", flexShrink: 0, marginTop: 1 }} />}
                <span style={{ fontSize: 13, color: msg.tone === "ok" ? "var(--ok)" : "var(--warn)", lineHeight: 1.5 }}>{msg.text}</span>
              </div>
            )}

            <div className="row wrap" style={{ gap: 10, marginTop: 18 }}>
              <button className="btn accent" onClick={save} disabled={busy === "save" || !key.trim()}>{busy === "save" ? "Saving…" : connected ? "Replace key" : "Connect"}<Icon.arrow width={15} /></button>
              {connected && <button className="btn ghost" onClick={test} disabled={!!busy}><Icon.swap width={14} />{busy === "test" ? "Testing…" : "Test connection"}</button>}
              {connected && info?.source === "app" && <button className="btn ghost" style={{ marginLeft: "auto", color: "var(--danger)", borderColor: "var(--danger)" }} onClick={clear}><Icon.close width={13} />Remove</button>}
            </div>

            <div className="dim" style={{ fontSize: 11.5, marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--line)", lineHeight: 1.6 }}>
              Find it in the Mind Map: sign in, open your browser's developer tools → Application → Local Storage → <span className="mono">taskmindmap:adminKey</span>. Only the admin can set this, and it's the one key that unlocks the whole bridge.
            </div>
          </div>
        )}

        <div className="ig-el row" style={{ justifyContent: "center", marginTop: 18 }}>
          <button className="link" onClick={() => nav(session ? "/studio/projects" : "/")} style={{ background: "none", border: "none", color: "var(--ink-3)", fontSize: 12.5, cursor: "pointer" }}>← Back to the studio</button>
        </div>
      </div>
    </div>
  );
}
